/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, Button, Link, Stack, TextField, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { signupAPI } from '../api/crm'
import { Link as RouterLink, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import AuthCard from '../components/common/AuthCard'
import SsoButtons, { SSO_ERRORS } from '../components/common/SsoButtons'
import { useDispatch, useSelector } from 'react-redux'
import { clearError, loginUser } from '../store/slices/authSlice'

const LoginPage = () => {
  const [searchParams] = useSearchParams()
  // only same-app paths: never redirect to another site after login
  const next = /^\/(?!\/)/.test(searchParams.get('next') || '') ? searchParams.get('next') : null
  const [email, setEmail] = useState(searchParams.get('email') || '')
  const [password, setPassword] = useState('')
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { loading, error, isAuthenticated } = useSelector((state) => state.auth)
  const signup = useQuery({ queryKey: ['signup', 'config'], queryFn: signupAPI.config, retry: false, staleTime: 10 * 60_000 })

  if (isAuthenticated) return <Navigate to={next || '/'} replace />

  const handleSubmit = async (e) => {
    e.preventDefault()
    const result = await dispatch(loginUser({ email: email.trim(), password }))
    if (loginUser.fulfilled.match(result)) navigate(next || '/select-tenant')
  }

  return (
    <AuthCard subtitle="Sign in to your workspace">
      {searchParams.get('reset') && !error && <Alert severity="success" sx={{ mb: 2 }}>Password changed. Sign in with your new password.</Alert>}
      {searchParams.get('sso_error') && !error && (
        <Alert severity="warning" sx={{ mb: 2 }}>{SSO_ERRORS[searchParams.get('sso_error')] || SSO_ERRORS.provider}</Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => dispatch(clearError())} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Email" type="email" autoComplete="email" autoFocus={!email} required value={email} onChange={(e) => setEmail(e.target.value)} />
        <TextField label="Password" type="password" autoComplete="current-password" autoFocus={!!email} required value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" variant="contained" size="medium" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </Button>
      </Box>
      <SsoButtons next={next} />
      <Stack direction="row" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 2 }}>
        <Link component={RouterLink} to={`/forgot-password${email ? `?email=${encodeURIComponent(email.trim())}` : ''}`} variant="body2">
          Forgot password?
        </Link>
        {signup.data?.enabled ? (
          <Typography variant="body2">New here? <Link component={RouterLink} to="/signup">Start a free trial</Link></Typography>
        ) : !signup.isLoading && (
          // sign-up closed: send newcomers to the website's demo page rather than a dead end
          <Typography variant="body2">No account yet? <Link href="https://credintel.in/demo.html">Book a demo</Link></Typography>
        )}
      </Stack>
    </AuthCard>
  )
}

export default LoginPage
